/**
 * @file xcf-utils.ts
 * @description Utilities and helper functions for GIMP XCF format decoding.
 */

/**
 * Decodes RLE-compressed tiled planar data from XCF.
 */
export function decodeTiledPlanar(data: Uint8Array, bpp: number, tw: number, th: number): Uint8Array[] {
  const channels: Uint8Array[] = [];
  let inPos = 0;
  const expectedChannelSize = tw * th;

  for (let c = 0; c < bpp; c++) {
    const out = new Uint8Array(expectedChannelSize);
    let outPos = 0;
    while (outPos < expectedChannelSize && inPos < data.length) {
      const b = data[inPos++];
      if (b < 127) {
        // Repeat Run
        const count = b + 1;
        const val = data[inPos++];
        const toFill = Math.min(count, expectedChannelSize - outPos);
        for (let i = 0; i < toFill; i++) {
          out[outPos++] = val;
        }
      } else if (b === 127) {
        // Long Repeat
        const count = (data[inPos] << 8 | data[inPos + 1]) + 1;
        inPos += 2;
        const val = data[inPos++];
        const toFill = Math.min(count, expectedChannelSize - outPos);
        for (let i = 0; i < toFill; i++) {
          out[outPos++] = val;
        }
      } else if (b === 128) {
        // Long Literal
        const count = (data[inPos] << 8 | data[inPos + 1]) + 1;
        inPos += 2;
        const toCopy = Math.min(count, expectedChannelSize - outPos);
        for (let i = 0; i < toCopy; i++) {
          out[outPos++] = data[inPos++];
        }
        if (toCopy < count) inPos += (count - toCopy);
      } else {
        // Short Literal
        const count = 256 - b;
        const toCopy = Math.min(count, expectedChannelSize - outPos);
        for (let i = 0; i < toCopy; i++) {
          out[outPos++] = data[inPos++];
        }
        if (toCopy < count) inPos += (count - toCopy);
      }
    }
    channels.push(out);
  }
  return channels;
}

/**
 * Maps XCF internal mode integers to standard composite mode strings.
 */
export function mapXcfBlendMode(mode: number): string {
  const modes: Record<number, string> = {
    0: "normal",
    1: "dissolve",
    2: "behind",
    3: "multiply",
    4: "screen",
    5: "overlay",
    6: "difference",
    7: "addition",
    8: "subtract",
    9: "darken-only",
    10: "lighten-only",
    11: "hue",
    12: "saturation",
    13: "color",
    14: "value"
  };
  return modes[mode] || "normal";
}

/**
 * Encodes raw channel data into XCF RLE format.
 */
export function encodeRle(data: Uint8Array): Uint8Array {
  const output: number[] = [];
  let pos = 0;
  const len = data.length;

  while (pos < len) {
    let run = 1;
    // Count consecutive identical bytes for repeat run
    while (pos + run < len && data[pos + run] === data[pos] && run < 32768) {
      run++;
    }

    if (run >= 2) {
      if (run <= 127) {
        output.push(run - 1);
        output.push(data[pos]);
      } else {
        output.push(127);
        output.push((run - 1) >> 8);
        output.push((run - 1) & 0xFF);
        output.push(data[pos]);
      }
      pos += run;
    } else {
      // Count unique bytes for literal run
      let literal = 1;
      while (pos + literal < len && 
             !(pos + literal + 1 < len && data[pos + literal] === data[pos + literal + 1]) && 
             literal < 32768) {
        literal++;
      }

      if (literal <= 127) {
        output.push(256 - literal);
        for (let i = 0; i < literal; i++) output.push(data[pos + i]);
      } else {
        output.push(128);
        output.push((literal - 1) >> 8);
        output.push((literal - 1) & 0xFF);
        for (let i = 0; i < literal; i++) output.push(data[pos + i]);
      }
      pos += literal;
    }
  }
  return new Uint8Array(output);
}

/**
 * Standard XCF blend modes to integer mapping for export.
 */
export function mapBlendModeToXcf(mode: string): number {
  const modes: Record<string, number> = {
    "normal": 0,
    "source-over": 0,
    "dissolve": 1,
    "behind": 2,
    "multiply": 3,
    "screen": 4,
    "overlay": 5,
    "difference": 6,
    "addition": 7,
    "subtract": 8,
    "darken-only": 9,
    "lighten-only": 10,
    "hue": 11,
    "saturation": 12,
    "color": 13,
    "value": 14
  };
  return modes[mode] || 0;
}
